import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useValidarDeudaCliente } from './useValidarDeudaCliente';

// --- ICONOS LOCALES SVG ---
const IconAlertTriangle = ({ className = "w-5 h-5 text-amber-600 shrink-0" }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

const IconChevronDown = ({ isOpen }) => (
    <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const IconFileText = () => (
    <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);

const IconClose = () => (
    <svg className="w-5 h-5 text-slate-400 hover:text-slate-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

// --- TABLA REUTILIZABLE DE PÓLIZAS ---
const TablaPolizasDeuda = ({ loading, polizas }) => {
    if (loading) {
        return (
            <div className="py-6 text-center">
                <div className="inline-block w-6 h-6 border-2 border-t-amber-600 border-amber-200 rounded-full animate-spin"></div>
                <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mt-2">
                    Consultando pólizas con deuda...
                </p>
            </div>
        );
    }

    if (!polizas || polizas.length === 0) {
        return (
            <div className="py-4 text-center text-xs text-slate-400">
                No se registraron pólizas pendientes detalladas.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
                <thead>
                    <tr className="text-[9px] uppercase font-black text-amber-800/70 border-b border-amber-200/50">
                        <th className="pb-2">Póliza</th>
                        <th className="pb-2">Compañía</th>
                        <th className="pb-2">Riesgo</th>
                        <th className="pb-2 text-center">Tipo Operación</th>
                        <th className="pb-2 text-right">Vencimiento</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-amber-200/40 font-semibold text-slate-700">
                    {polizas.map((poliza) => (
                        <tr key={poliza.id} className="hover:bg-amber-100/30 transition-colors">
                            <td className="py-2.5 flex items-center gap-1.5 font-mono font-bold text-amber-950">
                                <IconFileText />
                                {poliza.nPoliza}
                            </td>
                            <td className="py-2.5 uppercase text-slate-800 text-[11px]">
                                {poliza.compania}
                            </td>
                            <td className="py-2.5 uppercase text-slate-600 text-[11px]">
                                {poliza.riesgo} {poliza.subriesgo ? `(${poliza.subriesgo})` : ''}
                            </td>
                            <td className="py-2.5 text-center">
                                <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase ${
                                    poliza.motivo === 'Póliza Nueva' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                                    poliza.motivo === 'Renovación' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                    'bg-rose-100 text-rose-800 border border-rose-200'
                                }`}>
                                    {poliza.motivo}
                                </span>
                            </td>
                            <td className="py-2.5 text-right font-mono text-[11px] text-slate-700 font-bold">
                                {poliza.finVigencia}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export const AlertaDeudaCliente = ({ client, variant = 'banner', className = '' }) => {
    // 1. Hooks siempre arriba
    const { polizasAfectadas, loading, obtenerPolizasConDeuda } = useValidarDeudaCliente();
    const [desplegado, setDesplegado] = useState(false);
    const [modalAbierto, setModalAbierto] = useState(false);

    // 2. Retorno anticipado después de los hooks si no hay deuda
    if (!client?.tieneDeuda) return null;

    const cantidad = client.cantidadPolizasConDeuda || 1;

    // --- MANEJADORES ---
    const toggleDesplegarBanner = () => {
        if (!desplegado) {
            obtenerPolizasConDeuda(client.id);
        }
        setDesplegado(!desplegado);
    };

    const abrirModalBadge = (e) => {
        e.stopPropagation();
        obtenerPolizasConDeuda(client.id);
        setModalAbierto(true);
    };

    // --- VARIANTE 1: BADGE CON POP-UP / MODAL INFORMATIVO ---
    if (variant === 'badge') {
        return (
            <>
                <button
                    type="button"
                    onClick={abrirModalBadge}
                    title="Asegurado con mora pendiente. Clic para ver pólizas."
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-[10px] font-black uppercase tracking-wider shadow-2xs transition-all active:scale-95 cursor-pointer select-none ${className}`}
                >
                    <IconAlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                    <span>cliente con Deuda ({cantidad} póliza/s con mora)</span>
                </button>

                {/* MODAL / POP-UP FLOTANTE (Renderizado via Portal) */}
                {modalAbierto && createPortal(
                    <div 
                        className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-xs animate-fade-in text-left"
                        onClick={() => setModalAbierto(false)}
                    >
                        <div 
                            className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-2xl w-full p-6 space-y-4 animate-scale-up"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Cabecera del Pop-up */}
                            <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
                                        <IconAlertTriangle className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <h4 className="text-base font-black text-slate-900 uppercase tracking-tight">
                                            Pólizas con Deuda Pendiente
                                        </h4>
                                        <p className="text-xs text-slate-400 font-medium">
                                            Asegurado: <span className="font-bold text-slate-700 uppercase">{client.nombre} {client.apellido}</span> ({cantidad} póliza/s con mora)
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setModalAbierto(false)}
                                    className="p-1.5 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                                >
                                    <IconClose />
                                </button>
                            </div>

                            {/* Contenido con la tabla */}
                            <div className="max-h-[60vh] overflow-y-auto">
                                <TablaPolizasDeuda loading={loading} polizas={polizasAfectadas} />
                            </div>

                            {/* Pie del Pop-up */}
                            <div className="pt-2 flex justify-between items-center border-t border-slate-100">
                                <span className="text-[12px] font-medium text-amber-800/80">
                                    Se sugiere verificar los cobros antes de operar.
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setModalAbierto(false)}
                                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors shadow-xs cursor-pointer"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </>
        );
    }

    // --- VARIANTE 2: BANNER CORPORATIVO (POR DEFECTO) ---
    return (
        <div className={`bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/90 rounded-2xl p-4 shadow-sm text-left ${className}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <IconAlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h5 className="text-sm font-black text-amber-900 uppercase">
                                Cliente con deuda pendiente
                            </h5>
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-200/80 text-amber-900 border border-amber-300">
                                {cantidad} {cantidad === 1 ? 'Póliza con mora' : 'Pólizas con mora'}
                            </span>
                        </div>
                        <p className="text-[12px] font-medium text-amber-800/90 mt-0.5">
                            Este asegurado registra cuotas impagas en su cartera. Se sugiere regularizar la cobranza antes de emitir.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={toggleDesplegarBanner}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/90 hover:bg-white text-amber-900 border border-amber-200 text-[11px] font-bold tracking-wider uppercase shadow-2xs transition-all active:scale-95 shrink-0 cursor-pointer"
                >
                    <span>{desplegado ? 'Ocultar' : 'Ver Pólizas'}</span>
                    <IconChevronDown isOpen={desplegado} />
                </button>
            </div>

            {desplegado && (
                <div className="mt-3 pt-3 border-t border-amber-200/60">
                    <TablaPolizasDeuda loading={loading} polizas={polizasAfectadas} />
                </div>
            )}

            <style>{`
                @keyframes scaleUp {
                    from { opacity: 0; transform: scale(0.96); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-scale-up {
                    animation: scaleUp 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>
        </div>
    );
};

export default AlertaDeudaCliente;